import UploadIcon from '@mui/icons-material/Upload';
import { FC, useCallback, useEffect, useRef, useState } from 'react';

import { fetchLeadBrandCommunities, uploadLeadEmailAsset } from '../../api/leads';
import { LeadBrandCommunityOption, LeadEmailBrandAssets } from '../../data/types';
import { DEFAULT_LEAD_EMAIL_BRAND_ASSETS } from '../../utils/leadEmailHtml';

const IMAGE_ACCEPT = 'image/png,image/jpeg,image/gif,image/webp';

type BrandSlot = {
  key: keyof LeadEmailBrandAssets;
  label: string;
};

const BRAND_SLOTS: BrandSlot[] = [
  { key: 'logo_url', label: 'Logo' },
  { key: 'hero_image_url', label: 'Hero' },
  { key: 'feature_image_url', label: 'Feature' },
  { key: 'cta_image_url', label: 'CTA' },
];

type LeadEmailBrandPanelProps = {
  adminEmail: string;
  assets: LeadEmailBrandAssets;
  onChange: (assets: LeadEmailBrandAssets) => void;
  compact?: boolean;
};

/** Brand asset controls for leads CRM email composer (logo, hero, feature, CTA). */
export const LeadEmailBrandPanel: FC<LeadEmailBrandPanelProps> = ({
  adminEmail,
  assets,
  onChange,
  compact = false,
}) => {
  const fileInputRef = useRef<HTMLInputElement>(null);
  const uploadSlotRef = useRef<keyof LeadEmailBrandAssets | null>(null);
  const [communities, setCommunities] = useState<LeadBrandCommunityOption[]>([]);
  const [loadingCommunities, setLoadingCommunities] = useState(false);
  const [uploadingSlot, setUploadingSlot] = useState<string | null>(null);
  const [statusMessage, setStatusMessage] = useState('');

  useEffect(() => {
    setLoadingCommunities(true);
    fetchLeadBrandCommunities(adminEmail)
      .then((items) => setCommunities(items ?? []))
      .finally(() => setLoadingCommunities(false));
  }, [adminEmail]);

  const updateSlot = useCallback(
    (key: keyof LeadEmailBrandAssets, value: string) => {
      onChange({ ...assets, [key]: value });
    },
    [assets, onChange]
  );

  const handleCommunitySelect = (communityId: string) => {
    const community = communities.find((item) => item.id === communityId);
    if (!community) return;
    onChange({
      ...assets,
      logo_url: community.logo || assets.logo_url || DEFAULT_LEAD_EMAIL_BRAND_ASSETS.logo_url,
      hero_image_url:
        community.bgPhoto || assets.hero_image_url || DEFAULT_LEAD_EMAIL_BRAND_ASSETS.hero_image_url,
      source_community_id: community.id,
      source_community_name: community.name,
    });
    setStatusMessage(`Applied assets from ${community.name}.`);
  };

  const handleUploadClick = (slotKey: keyof LeadEmailBrandAssets) => {
    uploadSlotRef.current = slotKey;
    fileInputRef.current?.click();
  };

  const handleFileChange = async (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    event.target.value = '';
    const slotKey = uploadSlotRef.current;
    uploadSlotRef.current = null;
    if (!file || !slotKey) return;

    setUploadingSlot(slotKey);
    setStatusMessage('');
    try {
      const result = await uploadLeadEmailAsset(file, adminEmail, slotKey.replace('_url', ''));
      if (result?.url) {
        updateSlot(slotKey, result.url);
        setStatusMessage(`${BRAND_SLOTS.find((s) => s.key === slotKey)?.label ?? 'Image'} uploaded.`);
      } else {
        setStatusMessage('Upload failed. Try again.');
      }
    } catch {
      setStatusMessage('Upload failed. Try again.');
    } finally {
      setUploadingSlot(null);
    }
  };

  const handleReset = () => {
    onChange({ ...DEFAULT_LEAD_EMAIL_BRAND_ASSETS });
    setStatusMessage('Reset to HomeCook defaults.');
  };

  return (
    <div className={compact ? 'space-y-3' : 'mt-6 rounded-xl border border-[#E9E9E9] bg-white p-3'}>
      {!compact ? (
        <p className="mb-2 text-xs font-semibold uppercase tracking-wide text-[#737373] font-raleway">
          Brand assets
        </p>
      ) : null}

      <label className="block text-xs font-open-sans text-[#737373]">
        Community (optional)
        <select
          value={assets.source_community_id ?? ''}
          onChange={(event) => handleCommunitySelect(event.target.value)}
          className="mt-1 w-full rounded-lg border border-[#E9E9E9] px-2 py-1.5 text-sm font-open-sans"
          disabled={loadingCommunities}
        >
          <option value="">Select community…</option>
          {communities.map((community) => (
            <option key={community.id} value={community.id}>
              {community.name}
            </option>
          ))}
        </select>
      </label>

      <div className="mt-3 space-y-3">
        {BRAND_SLOTS.map((slot) => {
          const url = assets[slot.key] as string | undefined;
          return (
            <div key={slot.key} className="rounded-lg border border-[#F0F0F0] p-2">
              <div className="flex items-center justify-between gap-2">
                <p className="text-xs font-semibold text-[#013847] font-raleway">{slot.label}</p>
                <button
                  type="button"
                  className="inline-flex items-center gap-1 rounded-md border border-[#E9E9E9] px-2 py-1 text-[10px] font-semibold font-raleway disabled:opacity-50"
                  onClick={() => handleUploadClick(slot.key)}
                  disabled={uploadingSlot === slot.key}
                >
                  <UploadIcon sx={{ fontSize: 12 }} />
                  {uploadingSlot === slot.key ? 'Uploading…' : 'Upload'}
                </button>
              </div>
              {url ? (
                <img
                  src={url}
                  alt={`${slot.label} preview`}
                  className="mt-2 max-h-16 w-full rounded border border-[#E9E9E9] object-contain bg-[#FAFAFA]"
                />
              ) : null}
              <input
                value={url ?? ''}
                onChange={(event) => updateSlot(slot.key, event.target.value)}
                placeholder={`${slot.label} URL`}
                className="mt-2 w-full rounded-lg border border-[#E9E9E9] px-2 py-1.5 text-xs font-open-sans"
              />
            </div>
          );
        })}
      </div>

      <button
        type="button"
        onClick={handleReset}
        className="mt-3 w-full rounded-lg border border-[#E9E9E9] px-3 py-2 text-xs font-semibold font-raleway hover:bg-[#FAFAFA]"
      >
        Reset to HomeCook defaults
      </button>

      {statusMessage ? (
        <p className="mt-2 text-xs text-[#737373] font-open-sans">{statusMessage}</p>
      ) : null}

      <input
        ref={fileInputRef}
        type="file"
        accept={IMAGE_ACCEPT}
        className="hidden"
        onChange={handleFileChange}
      />
    </div>
  );
};
